import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  updateProfeciencyRequest,
  userSkillsRequest
} from "../../../remote/usersRequests";
import SkillsList from "./SkillsList";
import theme from "../../../components/LayoutComponents/theme";
import SubmitStatememt from "../../../components/FormComponents/SubmitStatement";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import { deleteSourceRequest } from "../../../remote/usersRequests";
import ProcessResumeButton from "../../../components/FormComponents/ProcessResumeButton";

const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 1rem',
    gap: 3,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    color: theme.colors.purple,
    mb: 1,
  },
  inputSection: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 2,
    padding: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  inputActions: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    mt: 2,
    flexWrap: 'wrap',
  },
  skillsSection: {
    width: '100%',
    maxWidth: '1000px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1,
    flexWrap: 'wrap',
    gap: 1,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px 8px 0 0',
    border: '1px solid #e0e0e0',
    borderBottom: 'none',
    flexWrap: 'wrap',
  },
  filterSelect: {
    minWidth: 130,
    backgroundColor: '#fff',
    '& .MuiSelect-select': {
      padding: '8px 12px',
      fontSize: '0.875rem',
    },
  },
  skillsContainer: {
    width: '100%',
  },
};

function MemberSkills({
  session,
  email,
  skills,
  setSkills,
  handleSkillsUpdate,
  proficiencies,
  isMobile
}) {

  const [foundationalFilter, setFoundationalFilter] = useState("");
  const [compTypeFilter, setCompTypeFilter] = useState("");
  const [profRatingFilter, setProfRatingFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [newlyAddedSkills, setNewlyAddedSkills] = useState([]);
  const [changedProficiencies, setChangedProficiencies] = useState([]);
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [feedback, setFeedback] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message, severity = 'success') => {
    setFeedback({ open: true, message, severity });
  };


  const uniqueFoundationalOrIndustry = useMemo(
    () => [...new Set(skills.map(skill => skill.competencyType))].sort(),
    [skills]
  );

  const uniqueCompTypes = useMemo(
    () => [...new Set(
      skills
        .filter(skill => !foundationalFilter || skill.competencyType === foundationalFilter)
        .map(skill => skill.competencyTier)
    )].sort(),
    [skills, foundationalFilter]
  );

  const uniqueProfRatings = useMemo(
    () => [...new Set(
      skills
        .filter(skill => !compTypeFilter || skill.competencyTier === compTypeFilter)
        .map(skill => skill?.proficiencyRating)
    )].sort((a, b) => a - b),
    [skills, compTypeFilter]
  );

  const uniqueSources = useMemo(
    () => [...new Set(skills.map(skill => skill.originalStatement).filter(Boolean))],
    [skills]
  );

  const handleProficiencyChange = (compXwalkId, compId, newProfRating) => {
    setChangedProficiencies((prev) => {
      const existingIndex = prev.findIndex(item => item.compXwalkId === compXwalkId);
      const updatedProficiency = {
        userId: session.id,
        compXwalkId,
        compId,
        proficiencyRating: newProfRating,
        modified: true,
      };
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedProficiency;
        return updated;
      }
      return [...prev, updatedProficiency];
    });

    setSkills((prevSkills) =>
      prevSkills.map(skill =>
        skill.compXwalkId === compXwalkId
          ? { ...skill, proficiencyRating: newProfRating, modified: true }
          : skill
      )
    );
  };

  const submitProficiencies = async () => {
    if (!changedProficiencies.length) {
      setFeedback({ open: true, message: 'No changes detected.', severity: 'warning' });
      return;
    }
    try {
      await updateProfeciencyRequest(session.token, changedProficiencies);
      setFeedback({ open: true, message: 'Proficiencies updated successfully!', severity: 'success' });

      // Fetch fresh data from backend to ensure ordering and filtering apply correctly
      const skillsResponse = await userSkillsRequest(session.token, session.userId);
      const freshSkills = skillsResponse.data || [];

      setSkills(freshSkills);
      handleSkillsUpdate(freshSkills);
      setChangedProficiencies([]);
    } catch (error) {
      console.error("Error updating proficiencies:", error);
      setFeedback({ open: true, message: 'An error occurred while updating proficiencies. Please try again.', severity: 'error' });
    }
  };

  const filteredSkills = useMemo(() => {
    return skills?.filter(skill => {
      const matchesFoundational = !foundationalFilter || skill?.competencyType === foundationalFilter;
      const matchesCompType = !compTypeFilter || skill?.competencyTier === compTypeFilter;
      const matchesProfRating = !profRatingFilter || skill?.proficiencyRating === Number(profRatingFilter);
      const matchesSource = !sourceFilter || skill?.originalStatement === sourceFilter;
      return matchesFoundational && matchesCompType && matchesProfRating && matchesSource;
    });
  }, [skills, foundationalFilter, compTypeFilter, profRatingFilter, sourceFilter]);

  useEffect(() => {
    if (!foundationalFilter) {
      setCompTypeFilter("");
      setProfRatingFilter("");
      setSourceFilter("");
    }
  }, [foundationalFilter]);

  const handleDelete = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this source and all of it corrsponding matches? This action cannot be undone.'
      )) {
      try {
        setLoading(true)
        let res = await deleteSourceRequest(session?.token, sourceFilter)
        setSkills(res.data || [])
        setFeedback({ open: true, message: `Deleted all matches associated with source: ${sourceFilter}`, severity: 'success' });
        setSourceFilter("")
      } catch (e) {
        setFeedback({ open: true, message: 'Delete match failed, try again.', severity: 'error' });
      } finally {
        setLoading(false)
      }
    }
  }

  const clearAllFilters = () => {
    setFoundationalFilter("");
    setCompTypeFilter("");
    setProfRatingFilter("");
    setSourceFilter("");
  };

  const hasActiveFilters = foundationalFilter || compTypeFilter || profRatingFilter || sourceFilter;

  const CompactFilterBar = (
    <Box sx={styles.filterBar}>
      <FilterListIcon sx={{ color: '#666', fontSize: '1.25rem' }} />
      <Typography sx={{ color: '#666', fontSize: '0.875rem', fontWeight: 500, mr: 1 }}>
        Filters:
      </Typography>
      
      <FormControl size="small" sx={styles.filterSelect}>
        <Select
          value={foundationalFilter}
          onChange={(e) => setFoundationalFilter(e.target.value)}
          displayEmpty
          renderValue={(value) => value || "Industry"}
        >
          <MenuItem value="">All Industries</MenuItem>
          {uniqueFoundationalOrIndustry.map((value) => (
            <MenuItem key={value} value={value}>{value}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={styles.filterSelect}>
        <Select
          value={compTypeFilter}
          onChange={(e) => setCompTypeFilter(e.target.value)}
          displayEmpty
          renderValue={(value) => value || "Type"}
        >
          <MenuItem value="">All Types</MenuItem>
          {uniqueCompTypes.map((value) => (
            <MenuItem key={value} value={value}>{value}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={styles.filterSelect}>
        <Select
          value={profRatingFilter}
          onChange={(e) => setProfRatingFilter(e.target.value)}
          displayEmpty
          renderValue={(value) => value ? `Rating: ${value}` : "Rating"}
        >
          <MenuItem value="">All Ratings</MenuItem>
          {uniqueProfRatings.map((value) => (
            <MenuItem key={value} value={value}>{value}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ ...styles.filterSelect, minWidth: 150 }}>
        <Select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          displayEmpty
          renderValue={(value) => {
            if (!value) return "Source";
            return value.length > 20 ? value.slice(0, 20) + "..." : value;
          }}
          MenuProps={{
            PaperProps: {
              style: { maxWidth: 400, maxHeight: 300 },
            },
          }}
        >
          <MenuItem value="">All Sources</MenuItem>
          {uniqueSources.map((value) => (
            <MenuItem key={value} value={value} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 350 }}>
              {value.length > 60 ? value.slice(0, 60) + "..." : value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasActiveFilters && (
        <Chip
          label="Clear all"
          size="small"
          onDelete={clearAllFilters}
          onClick={clearAllFilters}
          sx={{ 
            ml: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        />
      )}
    </Box>
  );

  return (
    <Box sx={styles.pageContainer}>
      {/* Page Title */}
      <Typography variant={isMobile ? "h5" : "h4"} sx={styles.title}>
        Skills
      </Typography>

      {/* Input Section - Statement + Resume Upload */}
      <Box sx={styles.inputSection}>
        <SubmitStatememt
          session={session}
          email={email}
          setSkills={setSkills}
          setNewlyAddedSkills={setNewlyAddedSkills}
          showToast={showToast}
        />
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">or</Typography>
        </Divider>
        <Box sx={styles.inputActions}>
          <ProcessResumeButton
            session={session}
            handleSkillsUpdate={handleSkillsUpdate}
            showToast={showToast}
          />
        </Box>
      </Box>

      {/* Skills Section */}
      {!skills ? (
        <Box display="flex" justifyContent="center" mt={4}><LoadingCircle /></Box>
      ) : skills.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No skills yet. Submit a statement or upload your resume to get started.
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.skillsSection}>
          {/* Section Header */}
          <Box sx={styles.sectionHeader}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                Your Skills
              </Typography>
              <Chip 
                label={`${filteredSkills?.length || 0} of ${skills.length}`} 
                size="small" 
                sx={{ backgroundColor: theme.colors.purple, color: '#fff', fontWeight: 500 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {sourceFilter && (
                <PurpleButton 
                  onClick={() => handleDelete('source')} 
                  label="Delete Filtered" 
                  size="small"
                />
              )}
              {changedProficiencies.length > 0 && (
                <Chip 
                  label={`${changedProficiencies.length} unsaved`}
                  size="small"
                  color="warning"
                />
              )}
              <PurpleButton 
                onClick={submitProficiencies} 
                label="Save Changes" 
                disabled={changedProficiencies.length === 0}
              />
            </Box>
          </Box>

          {/* Filter Bar + Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingCircle />
            </Box>
          ) : (
            <Box sx={styles.skillsContainer}>
              {CompactFilterBar}
              <SkillsList
                session={session}
                proficiencies={proficiencies}
                handleProficiencyChange={handleProficiencyChange}
                skills={filteredSkills}
                newlyAddedSkills={newlyAddedSkills}
                setSkills={setSkills}
                isMobile={isMobile}
                loading={loading}
                setLoading={setLoading}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
                showToast={showToast}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.severity}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MemberSkills;
